import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CategoryIcon from '@mui/icons-material/Category';
import {
  useGetCategoriesQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
} from '../../../store/api/productsApi';

function CategoriesManagement() {
  const [tabValue, setTabValue] = useState(0);
  const { data: categoriesData, refetch } = useGetCategoriesQuery({
    categoryType: tabValue === 0 ? 'raw' : 'finished'
  });
  const [createCategory] = useCreateCategoryMutation();
  const [updateCategory] = useUpdateCategoryMutation();
  const [deleteCategory] = useDeleteCategoryMutation();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    categoryType: 'raw',
    parentId: '',
  });

  const mainCategories = categoriesData?.filter((cat) => !cat.parentId) || [];
  const subCategories = categoriesData?.filter((cat) => cat.parentId) || [];

  const handleOpenDialog = (category = null) => {
    if (category && category.id) {
      // Editing existing category
      setEditingCategory(category);
      setFormData({
        name: category.name || '',
        code: category.code || '',
        description: category.description || '',
        categoryType: category.categoryType || 'raw',
        parentId: category.parentId || '',
      });
    } else if (category && category.parentId) {
      // Creating subcategory with pre-selected parent
      setEditingCategory(null);
      setFormData({
        name: '',
        code: '',
        description: '',
        categoryType: tabValue === 0 ? 'raw' : 'finished',
        parentId: category.parentId,
      });
    } else {
      // Creating new category
      setEditingCategory(null);
      setFormData({
        name: '',
        code: '',
        description: '',
        categoryType: tabValue === 0 ? 'raw' : 'finished',
        parentId: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCategory(null);
    setFormData({
      name: '',
      code: '',
      description: '',
      categoryType: 'raw',
      parentId: '',
    });
  };

  const handleSubmit = async () => {
    try {
      const submitData = {
        ...formData,
        parentId: formData.parentId || null,
      };
      if (editingCategory && editingCategory.id) {
        await updateCategory({ id: editingCategory.id, ...submitData }).unwrap();
      } else {
        await createCategory(submitData).unwrap();
      }
      refetch();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving category:', error);
      alert(error?.data?.message || 'Error saving category');
    }
  };

  const handleDelete = async (categoryId) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        await deleteCategory(categoryId).unwrap();
        refetch();
      } catch (error) {
        alert(error?.data?.message || 'Error deleting category');
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          Categories Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ borderRadius: 2 }}
        >
          Add Category
        </Button>
      </Box>

      <Tabs
        value={tabValue}
        onChange={(e, val) => setTabValue(val)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Raw Material Categories" />
        <Tab label="Finished Good Categories" />
      </Tabs>

      <Grid container spacing={3}>
        {/* Main Categories */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                Main Categories
              </Typography>
              {mainCategories.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Subcategories</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {mainCategories.map((category) => {
                        const childrenCount = subCategories.filter(
                          (sub) => sub.parentId === category.id
                        ).length;
                        return (
                          <TableRow key={category.id} hover>
                            <TableCell sx={{ fontWeight: 500 }}>{category.name}</TableCell>
                            <TableCell>{category.code || '-'}</TableCell>
                            <TableCell>
                              <Chip label={childrenCount} size="small" color="primary" />
                            </TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleOpenDialog(category)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDelete(category.id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  No main categories. Create one to get started.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Category Tree View */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                Category Hierarchy
              </Typography>
              {mainCategories.length > 0 ? (
                mainCategories.map((mainCategory) => {
                  const children = subCategories.filter(
                    (sub) => sub.parentId === mainCategory.id
                  );
                  return (
                    <Accordion key={mainCategory.id} sx={{ mb: 1 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CategoryIcon color="primary" />
                          <Typography sx={{ fontWeight: 600 }}>
                            {mainCategory.name}
                          </Typography>
                          {mainCategory.code && (
                            <Chip label={mainCategory.code} size="small" />
                          )}
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        {children.length > 0 ? (
                          <Box>
                            {children.map((subCategory) => (
                              <Box
                                key={subCategory.id}
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  p: 1,
                                  mb: 1,
                                  bgcolor: 'grey.50',
                                  borderRadius: 1,
                                }}
                              >
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {subCategory.name}
                                  </Typography>
                                  {subCategory.code && (
                                    <Typography variant="caption" color="text.secondary">
                                      {subCategory.code}
                                    </Typography>
                                  )}
                                </Box>
                            <Box>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleOpenDialog(subCategory)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDelete(subCategory.id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                              </Box>
                            ))}
                            <Button
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={() => handleOpenDialog({ parentId: mainCategory.id })}
                              sx={{ mt: 1 }}
                            >
                              Add Subcategory
                            </Button>
                          </Box>
                        ) : (
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              No subcategories
                            </Typography>
                            <Button
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={() => handleOpenDialog({ parentId: mainCategory.id })}
                            >
                              Add Subcategory
                            </Button>
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  No categories. Create a main category first.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCategory ? 'Edit Category' : 'Create Category'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Category Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Category Code"
                name="code"
                value={formData.code}
                onChange={handleChange}
                placeholder="Optional"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                select
                label={formData.parentId ? 'Main Category (cannot change)' : 'Main Category (optional)'}
                name="parentId"
                value={formData.parentId}
                onChange={handleChange}
                disabled={!!editingCategory?.parentId}
                helperText={
                  formData.parentId
                    ? 'This will be a subcategory'
                    : 'Leave empty to create a main category'
                }
              >
                <MenuItem value="">None (Main Category)</MenuItem>
                {mainCategories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                select
                label="Category Type"
                name="categoryType"
                value={formData.categoryType}
                onChange={handleChange}
                required
              >
                <MenuItem value="raw">Raw Material Category</MenuItem>
                <MenuItem value="finished">Finished Good (Menu Item) Category</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingCategory ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CategoriesManagement;
